import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useField, useFormikContext } from "formik";

const AsyncFormikDropdown = ({
    label,
    name,
    apiCallFunction, // Function to fetch data based on input
    placeholder = "Select",
    required = false,
    needSearch = false,
    debounceTime = 300, // Debounce for API calls
    ...props
}) => {
    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(name);

    const [inputValue, setInputValue] = useState(""); // Tracks user's input
    const [options, setOptions] = useState([]); // Stores dropdown options
    const [isLoading, setIsLoading] = useState(false); // For loader while fetching

    // Function to handle debounce for input changes
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (inputValue && apiCallFunction) {
                fetchOptions(inputValue);
            }
        }, debounceTime);

        return () => clearTimeout(delayDebounce); // Cleanup timeout
    }, [inputValue]);

    // Function to fetch options via API
    const fetchOptions = async (input) => {
        try {
            setIsLoading(true);
            const response = await apiCallFunction(input); // Call the provided function
            if (Array.isArray(response)) {
                setOptions(response);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error("Error fetching options:", error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (selectedOption) => {
        setFieldValue(name, selectedOption ? selectedOption.value : "");
        if (props.onChange) {
            props.onChange(selectedOption); // Call the parent onChange if provided
        }
    };

    return (
        <div className="form-control flex flex-col gap-2 p-2 w-full">
            {/* Label */}
            {label && (
                <label htmlFor={name} className="font-medium flex items-center">
                    {label}
                    {required && <span className="text-red-600 font-bold ml-1">*</span>}
                </label>
            )}
            <Select
                options={options}
                value={options.find((option) => option.value === field.value) || null}
                onChange={handleChange}
                onInputChange={(value) => setInputValue(value)} // Update inputValue on user typing
                classNamePrefix="react-select"
                placeholder={placeholder}
                isSearchable={needSearch}
                isLoading={isLoading} // Loader for async calls
                noOptionsMessage={() => "No options available"}
                className="w-full"
            />

            {/* Error Message */}
            {meta.touched && meta.error && (
                <p className="mt-2 text-sm text-red-500" id={`${name}-error`}>
                    {meta.error}
                </p>
            )}
        </div>
    );
};

export default AsyncFormikDropdown;
